import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { IconButton, Tooltip } from '@mui/material'
import cn from 'classnames'
import ColorThief from 'colorthief'
import React, { useEffect, useState } from 'react'
import config from '../../config.json'
import Icon from '../common/util/input/Icon'
import Link from '../common/util/input/Link'
import {
    formatAssetData,
    getImageData,
    getViewer,
    rgbToHex,
} from '../helpers/FormatLinks'
import VideoElement from '../videoelement/VideoElement'
import FullImageView from '../fullimageview/FullImageView'

export const AssetImage = ({ asset, title }) => {
    const [displayImage, setDisplayImage] = useState(null)
    const [displayVideo, setDisplayVideo] = useState(null)

    let { assetId, image, collection, mutableData, immutableData } = asset

    const assetData = formatAssetData(
        mutableData ? mutableData : immutableData ? immutableData : null,
    )

    let format = null

    const [imagePosition, setImagePosition] = useState(0)
    const [modelViewer, setModelViewer] = useState(null)

    const IMG_CLASS = 'm-auto max-h-64 md:max-h-[70vh] md:py-[2.5vh] max-w-full'

    const images = []
    const imageFormats = []

    let video = ''
    let audio = ''
    let model = ''
    if (
        assetData &&
        Object.keys(assetData).includes('video') &&
        assetData['video']
    ) {
        video = assetData['video'].includes('http')
            ? assetData['video']
            : `${config.ipfs}${assetData['video']}`
    } else if (
        assetData &&
        Object.keys(assetData).includes('Video') &&
        assetData['Video']
    ) {
        video = assetData['Video'].includes('http')
            ? assetData['Video']
            : `${config.ipfs}${assetData['Video']}`
    } else if (
        assetData &&
        Object.keys(assetData).includes('media') &&
        assetData['media']
    ) {
        video = assetData['media'].includes('http')
            ? assetData['media']
            : `${config.ipfs}${assetData['media']}`
    } else if (
        assetData &&
        Object.keys(assetData).includes('3dimg') &&
        assetData['3dimg']
    ) {
        model = assetData['3dimg'].includes('http')
            ? assetData['3dimg']
            : `${config.ipfs}${assetData['3dimg']}`
    } else if (
        assetData &&
        Object.keys(assetData).includes('3d') &&
        assetData['3d']
    ) {
        model = assetData['3d'].includes('http')
            ? assetData['3d']
            : `${config.ipfs}${assetData['3d']}`
    } else if (
        assetData &&
        Object.keys(assetData).includes('model') &&
        assetData['model']
    ) {
        model = assetData['model'].includes('http')
            ? assetData['model']
            : `${config.ipfs}${assetData['model']}`
    }

    if (assetData && Object.keys(assetData).includes('3dimg')) {
        model = assetData['3dimg'].includes('http')
            ? assetData['3dimg']
            : `${config.ipfs}${assetData['3dimg']}`
    }

    if (assetData && Object.keys(assetData).includes('audio')) {
        audio = assetData['audio'].includes('http')
            ? assetData['audio']
            : `${config.ipfs}${assetData['audio']}`
    }

    if (video) {
        images.push(
            video.replace(
                'https://gateway.pinata.cloud/ipfs/',
                'https://ipfs.hivebp.io/ipfs/',
            ),
        )
        imageFormats.push('video')
    }

    if (image && !image.includes('video:')) {
        images.push(
            image.replace(
                'https://gateway.pinata.cloud/ipfs/',
                'https://ipfs.hivebp.io/ipfs/',
            ),
        )
        imageFormats.push('image')
    }

    if (model) {
        images.push(model)
        imageFormats.push('model')
    }

    if (audio) {
        images.push(audio)
        imageFormats.push('video')
    }

    getImageData(images, imageFormats, assetData, format)

    useEffect(() => {
        if (imagePosition >= images.length) {
            setImagePosition(0)
        }
        if (imageFormats[imagePosition] === 'model') {
            getViewer(images[imagePosition], image).then(setModelViewer)
        }
    }, [imagePosition, image, assetId])

    const [bgColor, setBgColor] = useState('#000000')

    useEffect(() => {
        if (images && images[0]) {
            const universalPreview = config.ipfspreviewhash
                .replace('{collection}', collection.collectionName)
                .replace('{size}', 80)
                .replace('{hash}', images[0])
            const colorThief = new ColorThief()
            const img = new Image()
            img.addEventListener('load', () => {
                try {
                    const color = colorThief.getColor(img, 30)
                    const rgb = rgbToHex(color[0], color[1], color[2])
                    setBgColor(rgb)
                } catch (e) {
                    console.error(e)
                }
            })
            let imageURL = universalPreview
            let googleProxyURL =
                'https://images1-focus-opensocial.googleusercontent.com/gadgets/proxy?container=focus&refresh=2592000&url='
            img.crossOrigin = 'Anonymous'
            img.src = googleProxyURL + encodeURIComponent(imageURL)
        }
    }, [assetId, images])

    const toggleImage = (img) => {
        setDisplayImage(img)
    }

    const toggleVideo = (video) => {
        setDisplayVideo(video)
    }

    return images && images.length > 0 ? (
        <div
            className={cn(
                'w-full h-full border bg-white dark:!bg-paper border-zinc-200 dark:!border-paperl shadow-xl rounded-md mt-4',
            )}
        >
            <FullImageView
                image={displayImage}
                video={displayVideo}
                toggleFunc={() => {
                    setDisplayImage(null)
                    setDisplayVideo(null)
                }}
            />
            <div className="p-2 h-1/5 w-full flex border-b border-zinc-200 dark:!border-paperl">
                <div className="w-1/10" />
                <div className="w-1/2 my-auto text-lg text-center lg:w-8/10">
                    {title}
                </div>
                <div className="flex my-auto w-1/10 justify-evenly md:justify-end">
                    {asset.assetId && asset.templateId ? (
                        <div className="my-auto">
                            <Link
                                href={`/template/${
                                    asset.templateId ? asset.templateId : null
                                }`}
                                external
                            >
                                <IconButton size="small">
                                    <Tooltip title="Go to Template">
                                        <OpenInNewIcon />
                                    </Tooltip>
                                </IconButton>
                            </Link>
                        </div>
                    ) : (
                        <IconButton disabled size="small">
                            <OpenInNewIcon />
                        </IconButton>
                    )}
                    <div className="my-auto">
                        {asset.image ? (
                            <Link href={asset.image} external>
                                <IconButton size="small">
                                    <Tooltip title="Show Full Image">
                                        <OpenInFullIcon />
                                    </Tooltip>
                                </IconButton>
                            </Link>
                        ) : null}
                    </div>
                </div>
            </div>
            <div className="relative group h-4/5">
                <div
                    className={cn(
                        'hover:grayscale-0 flex flex-wrap rounded-b-md',
                        'w-full h-full justify-center',
                    )}
                >
                    {imageFormats[imagePosition] === 'video' ? (
                        <VideoElement
                            className={IMG_CLASS}
                            image={image}
                            video={images[imagePosition]}
                            autoPlay={true}
                            timeout={0}
                            useGradient={false}
                            fetchPriority={'high'}
                            lazy={false}
                            onClick={() => toggleVideo(images[imagePosition])}
                        />
                    ) : imageFormats[imagePosition] === 'model' ? (
                        modelViewer
                    ) : (
                        <img
                            onClick={() => toggleImage(images[imagePosition])}
                            src={
                                images[imagePosition].includes('http')
                                    ? images[imagePosition]
                                    : config.ipfs + images[imagePosition]
                            }
                            alt="none"
                            className={IMG_CLASS}
                        />
                    )}
                    {images.length > 1 && (
                        <div
                            className={cn(
                                'relative w-full p-4 flex justify-evenly ',
                            )}
                        >
                            {images.map((image, index) => (
                                <div
                                    className={cn(
                                        'w-7 h-7 text-2xl text-white',
                                        'cursor-pointer bg-transparent border-none outline-none',
                                    )}
                                    onClick={() => {
                                        setImagePosition(index)
                                    }}
                                >
                                    <Icon>
                                        <img
                                            className={
                                                'w-5 h-5 hidden dark:!block'
                                            }
                                            src={
                                                index === imagePosition
                                                    ? '/radio-button-on.svg'
                                                    : '/radio-button-off.svg'
                                            }
                                            alt="radio"
                                            width={150}
                                            height={150}
                                        />
                                        <img
                                            className={
                                                'w-5 h-5 block dark:!hidden'
                                            }
                                            src={
                                                index === imagePosition
                                                    ? '/radio-button-on.svg'
                                                    : '/radio-button-off-black.svg'
                                            }
                                            alt="radio"
                                        />
                                    </Icon>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    ) : (
        <div
            className={cn(
                'w-full h-full border bg-white dark:!bg-paper border-zinc-200 dark:!border-paperl shadow-xl rounded-md mt-4',
            )}
        />
    )
}
